/// <amd-module name="Build/Extensions/Connection-Services/ServiceConnectionRetrieverService" />
import { RepositoryTypes } from "Build/SourceProviders/Common";

import { localeComparer } from "VSS/Core/Util/String";
import { IVssObservableService, IVssPageContext, Services, VssObservableService } from "VSS/Platform/Context";
import {
    ContributionServiceEvent,
    IDataProviderQueryCompletedEvent,
    IServerException,
    IVssContributionService
} from "VSS/Platform/Contribution";

export interface ServiceConnectionInfo {
    id: string;
    scheme: string;
    name: string;
    imageUrl: string;
    manageAccessUrl: string;
    displayName : string;
    generatedBy: string;
    hostname: string;
}

export interface ServiceConnectionData {
    serviceConnections: ServiceConnectionInfo[];
}

export interface IServiceConnectionRetrieverService extends IVssObservableService<ServiceConnectionInfo[] | any> {
    updateServiceConnections(sourceProvider: string): void;
}

export const enum Actions {
    getServiceConnections = "getServiceConnections",
    error = "error",
}

export const noConnectionsPath = "ms.vss-build-web/connection-services/Content/No-Connections.png";

const SourceProviderConnections: { [key: string]: string } = {
    [RepositoryTypes.Bitbucket]: "ms.vss-build-web.bitbucket-serviceconnections-data-provider",
    [RepositoryTypes.GitHub]: "ms.vss-build-web.github-serviceconnections-data-provider",
    [RepositoryTypes.GitHubEnterprise]: "ms.vss-build-web.github-serviceconnections-data-provider"
}

export class ServiceConnectionRetrieverService extends VssObservableService<ServiceConnectionInfo[] | any> implements IServiceConnectionRetrieverService{
    private _started: boolean;
    private _contributionService: IVssContributionService;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._started = true;
        this._contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._contributionService.subscribe(this._onDataProviderQueryEnd, ContributionServiceEvent.DataProviderQueryCompleted);
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        this._started = false;
        this._contributionService.unsubscribe(this._onDataProviderQueryEnd, ContributionServiceEvent.DataProviderQueryCompleted);
    }

    public updateServiceConnections(sourceProvider: string): void {
        this._contributionService.getDataAsync<ServiceConnectionData>(SourceProviderConnections[sourceProvider], { "endpointType": sourceProvider }, true)
            .then((serviceConnectionData) => {
                let serviceConnections: ServiceConnectionInfo[] = [];
                if (serviceConnectionData) {
                    serviceConnections = serviceConnectionData.serviceConnections || [];
                    serviceConnections.sort((a, b) => localeComparer(a.name, b.name));
                }

                if (this._started) {
                    this._notify(serviceConnections, Actions.getServiceConnections);
                }
            });
    }

    private _onDataProviderQueryEnd = (queryEndEvent: IDataProviderQueryCompletedEvent) => {
        if (!this._started) {
            return;
        }
        const seen = new Set();
        Object.keys(SourceProviderConnections).forEach(key => {
            const dataProviderId = SourceProviderConnections[key];
            if (dataProviderId && queryEndEvent.dataProviderExceptions && !seen.has(dataProviderId)) {
                seen.add(dataProviderId);
                const exception: IServerException = queryEndEvent.dataProviderExceptions[dataProviderId];
                if (exception) {
                    this._notify(exception, Actions.error);
                }
            }
        });
    }
}

Services.add("IServiceConnectionRetrieverService", { serviceFactory: ServiceConnectionRetrieverService });
